import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

export type ValorFiltro = string | number | boolean | null;

/** Chaves de navegação: ficam na URL mas não contam como "filtro ativo". */
const NAVEGACAO = ["pagina", "tamanhoPagina", "ordenarPor", "direcao"];
/** O "Limpar filtros" mantém ordenação e tamanho de página. */
const PRESERVAR_AO_LIMPAR = ["tamanhoPagina", "ordenarPor", "direcao"];

type Largo<V> = V extends string ? string : V extends number ? number : V extends boolean ? boolean : string | null;
type Filtros<T> = { [K in keyof T]: Largo<T[K]> };

function ler(bruto: string | null, padrao: ValorFiltro): ValorFiltro {
  if (bruto === null) return padrao;
  if (typeof padrao === "number") {
    const n = Number(bruto);
    return Number.isFinite(n) ? n : padrao;
  }
  if (typeof padrao === "boolean") return bruto === "true" || bruto === "1";
  return bruto;
}

function ehPadrao(valor: ValorFiltro | undefined, padrao: ValorFiltro): boolean {
  if (valor === undefined || valor === null || valor === "") return padrao === null || padrao === "" || valor === padrao;
  return valor === padrao;
}

/**
 * Estado de filtros da consulta guardado na query string (`?busca=x&situacao=todos`),
 * para que voltar da edição (ou recarregar/compartilhar o link) mantenha a consulta.
 * Só valores diferentes do padrão vão para a URL. O tipo de cada filtro vem do padrão:
 * número → `Number`, booleano → `true/false`, texto/`null` → texto.
 *
 * Mudar qualquer filtro que não seja `pagina` volta para a página 1 (se houver `pagina`).
 */
export function useFiltrosNaUrl<T extends Record<string, ValorFiltro>>(padroes: T) {
  const [params, setParams] = useSearchParams();
  const chavePadroes = JSON.stringify(padroes);
  const textoParams = params.toString();

  const filtros = useMemo(() => {
    const out: Record<string, ValorFiltro> = {};
    for (const [k, padrao] of Object.entries(padroes)) out[k] = ler(params.get(k), padrao);
    return out as Filtros<T>;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [textoParams, chavePadroes]);

  const setFiltros = useCallback((patch: Partial<Filtros<T>>) => {
    setParams((atual) => {
      const novo = new URLSearchParams(atual);
      const mudouFiltro = Object.keys(patch).some((k) => k !== "pagina" && k !== "tamanhoPagina");
      const tudo: Record<string, ValorFiltro | undefined> = { ...patch };
      if (mudouFiltro && "pagina" in padroes && !("pagina" in patch)) tudo.pagina = padroes.pagina;
      for (const [k, v] of Object.entries(tudo)) {
        if (ehPadrao(v, padroes[k] ?? null)) novo.delete(k);
        else novo.set(k, String(v));
      }
      return novo;
    }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setParams, chavePadroes]);

  const setFiltro = useCallback(<K extends keyof T>(chave: K, valor: Filtros<T>[K]) =>
    setFiltros({ [chave]: valor } as unknown as Partial<Filtros<T>>), [setFiltros]);

  const limparFiltros = useCallback(() => {
    setParams((atual) => {
      const novo = new URLSearchParams(atual);
      for (const k of Object.keys(padroes)) if (!PRESERVAR_AO_LIMPAR.includes(k)) novo.delete(k);
      return novo;
    }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setParams, chavePadroes]);

  const filtrosAtivos = useMemo(
    () => Object.keys(padroes).filter((k) => !NAVEGACAO.includes(k) && !ehPadrao(filtros[k], padroes[k])).length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filtros, chavePadroes],
  );

  return { filtros, setFiltros, setFiltro, limparFiltros, filtrosAtivos };
}
